import { Link } from "react-router-dom";
import { CalendarHeart, Quote, HeartHandshake, ExternalLink, Youtube, ArrowRight } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { CtaBanner } from "@/components/CtaBanner";
import { CREDENTIALS, YOUTUBE_CHANNEL } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

export default function About() {
  const { t, to } = useLang();
  const a = t.about;

  return (
    <>
      <section data-testid="about-hero" className="pt-32 sm:pt-40 pb-16 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <FadeUp className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute -inset-4 rounded-t-[10rem] rounded-b-[2rem] border border-sage/30 pointer-events-none" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-t-[9rem] rounded-b-3xl bg-cream shadow-[0_40px_80px_-40px_rgba(44,37,32,0.45)]">
                <img
                  src="/images/natalia-portrait.jpg"
                  alt={a.portraitAlt}
                  data-testid="about-portrait"
                  className="w-full h-[24rem] sm:h-[28rem] object-cover object-[50%_22%]"
                />
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7 order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{a.kicker}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-balance">
              {a.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-clay leading-relaxed max-w-2xl">{a.intro}</p>
            <Link
              to={to("/booking")}
              data-testid="about-book-btn"
              className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5"
            >
              <CalendarHeart className="w-5 h-5" />
              {a.bookCta}
            </Link>
          </FadeUp>
        </div>
      </section>

      <section data-testid="about-mission-section" className="bg-cream/70 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <FadeUp>
            <Quote className="w-8 h-8 text-terracotta/50 mb-5" />
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taupe mb-4">{a.missionKicker}</p>
            <p className="font-serif text-2xl sm:text-3xl leading-snug text-espresso" data-testid="about-mission-text">
              {t.bio.mission}
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:pt-16">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taupe mb-4">{a.approachKicker}</p>
            <p className="text-base sm:text-lg text-clay leading-relaxed" data-testid="about-approach-text">
              {t.bio.approach}
            </p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="about-why-adhd-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <FadeUp className="lg:col-span-5">
            <span className="w-14 h-14 rounded-2xl bg-terracotta-soft flex items-center justify-center mb-6">
              <HeartHandshake className="w-6 h-6 text-terracotta" />
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-balance">{a.whyTitle}</h2>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7 space-y-5 text-base sm:text-lg text-clay leading-relaxed">
            <p>{a.whyP1}</p>
            <p>{a.whyP2}</p>
            <Link
              to={to("/podcast")}
              data-testid="about-podcast-card"
              className="group mt-2 flex items-center gap-5 rounded-3xl bg-white border border-border p-6 shadow-[0_16px_40px_-24px_rgba(44,37,32,0.2)] transition-transform duration-300 hover:-translate-y-1"
            >
              <span className="w-14 h-14 shrink-0 rounded-2xl bg-terracotta-soft flex items-center justify-center">
                <Youtube className="w-6 h-6 text-terracotta" />
              </span>
              <span>
                <span className="font-serif text-xl font-semibold block">{a.podcast.title}</span>
                <span className="block text-sm text-clay leading-relaxed mt-1.5">{a.podcast.text}</span>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-terracotta group-hover:text-terracotta-dark transition-colors">
                  {a.podcast.link} <ArrowRight className="w-4 h-4" />
                </span>
              </span>
            </Link>
          </FadeUp>
        </div>
      </section>

      <section data-testid="about-credentials-section" className="bg-cream/70 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
          <FadeUp className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{a.credsKicker}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{a.credsTitle}</h2>
            <p className="mt-4 text-base text-clay leading-relaxed">{a.credsIntro}</p>
          </FadeUp>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 max-w-3xl">
            {CREDENTIALS.map((c) => (
              <StaggerItem key={c.id}>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`credential-${c.id}`}
                  className="group flex items-center gap-5 rounded-3xl bg-white border border-border p-6 shadow-[0_16px_40px_-24px_rgba(44,37,32,0.2)] transition-transform duration-300 hover:-translate-y-1"
                >
                  <span className="w-20 h-20 rounded-2xl bg-badgebg flex items-center justify-center overflow-hidden shrink-0">
                    <img src={c.img} alt={c.name} className="w-16 h-16 object-contain" loading="lazy" />
                  </span>
                  <span>
                    <span className="font-serif text-xl font-semibold flex items-center gap-2">
                      {c.name}
                      <ExternalLink className="w-4 h-4 text-taupe opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                    <span className="block text-sm text-taupe mt-1">{c.detail}</span>
                  </span>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section data-testid="about-methodology-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
        <FadeUp className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{a.methodKicker}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{a.methodTitle}</h2>
        </FadeUp>
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.chapters.map((c) => (
            <StaggerItem key={c.num}>
              <div data-testid={`about-chapter-${c.num}`} className="h-full rounded-3xl bg-white border border-border p-7">
                <p className="font-serif text-5xl text-terracotta/40 font-semibold">{c.num}</p>
                <h3 className="font-serif text-xl font-semibold mt-4">{c.title}</h3>
                <p className="mt-3 text-sm text-clay leading-relaxed">{c.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <CtaBanner testid="about-cta-banner" title={a.cta.title} subtitle={a.cta.subtitle} />
    </>
  );
}
